/* istanbul ignore file */
import { products } from '@gojek/web-mokapos-protos';
import { detect } from 'detect-browser';

import {
  apiHealthEventConstants,
  componentEventConstants,
  pageEventConstants,
  inventoryTransferConstants,
  paymentConfigurationConstants,
  ingredientLibraryConstants,
  billingEventConstants,
} from './constants';
import { generateDevice, generateURL, getTimestampInSeconds, transformChangesArray, transformFilter } from './helpers';
import {
  Changes,
  Filter,
  Merchant,
  InventoryTransferDetail,
  PaymentConfigurationDetail,
  IngredientLibraryDetail,
  ErrorDetail,
  Billing,
} from './types';

const { Page, Component } = products.events.ui;
const { InventoryTransfer, PaymentConfiguration, IngredientLibrary, APIHealth } = products.events.business;

const PRODUCT_MAPPING = {
  unspecified: 0,
  mokapos: 1,
};

const PAGE_EVENT_MAPPING = {
  unspecified: 0,
  viewed: 1,
  error: 2,
  success: 3,
  redirected: 4,
};

const ACTION_EVENT_MAPPING = {
  unspecified: 0,

  // View Actions
  viewed: 10,
  loading: 11,
  loaded: 12,
  submitting: 13,
  submitted: 14,

  // Click Actions
  click: 20,
  open: 21,
  close: 22,
  submit: 23,
  change: 24,
  create: 25,

  // Navigation Actions
  navigate: 30,
  back: 31,
  forward: 32,
  refresh: 33,
};

function eventTrackerFactory() {
  const browser = detect();

  return {
    async trackPage(
      eventName: keyof typeof pageEventConstants,
      options?: {
        category?: keyof typeof PAGE_EVENT_MAPPING;
        product?: keyof typeof PRODUCT_MAPPING;
        filter?: Filter;
        merchant?: Merchant;
        useTimestamp?: boolean;
      },
    ) {
      const useTimestamp = options?.useTimestamp ?? true;

      return window.clickstream?.track(
        Page.create({
          eventName: pageEventConstants[eventName],
          product: PRODUCT_MAPPING[options?.product ?? 'mokapos'],
          category: PAGE_EVENT_MAPPING[options?.category ?? 'viewed'],
          filter: transformFilter(options?.filter),
          meta: {
            url: generateURL(),
            device: generateDevice(browser),
            merchant: options?.merchant,
          },
          ...(useTimestamp
            ? {
                eventTimestamp: {
                  seconds: getTimestampInSeconds(),
                },
              }
            : {}),
        }),
      );
    },
    async trackComponent(
      eventName: keyof typeof componentEventConstants,
      options?: {
        action?: keyof typeof ACTION_EVENT_MAPPING;
        product?: keyof typeof PRODUCT_MAPPING;
        detail?: {
          name?: string;
          parentComponentName?: string;
          pageName?: string;
        };
        filter?: Filter;
        changes?: Changes[];
        merchant?: Merchant;
        useTimestamp?: boolean;
      },
    ) {
      const useTimestamp = options?.useTimestamp ?? true;

      return window.clickstream?.track(
        Component.create({
          eventName: componentEventConstants[eventName],
          product: PRODUCT_MAPPING[options?.product ?? 'mokapos'],
          action: ACTION_EVENT_MAPPING[options?.action ?? 'click'],
          detail: options?.detail,
          filter: transformFilter(options?.filter),
          changes: transformChangesArray(options?.changes),
          meta: {
            url: generateURL(),
            device: generateDevice(browser),
            merchant: options?.merchant,
          },
          ...(useTimestamp
            ? {
                eventTimestamp: {
                  seconds: getTimestampInSeconds(),
                },
              }
            : {}),
        }),
      );
    },
    async trackInventoryTransfer(
      eventName: keyof typeof inventoryTransferConstants,
      options?: {
        action?: keyof typeof ACTION_EVENT_MAPPING;
        product?: keyof typeof PRODUCT_MAPPING;
        detail?: InventoryTransferDetail;
        filter?: Filter;
        merchant?: Merchant;
        useTimestamp?: boolean;
      },
    ) {
      const useTimestamp = options?.useTimestamp ?? true;

      return window.clickstream?.track(
        InventoryTransfer.create({
          eventName: inventoryTransferConstants[eventName],
          product: PRODUCT_MAPPING[options?.product ?? 'mokapos'],
          action: ACTION_EVENT_MAPPING[options?.action ?? 'click'],
          filter: transformFilter(options?.filter),
          detail: options?.detail,
          merchant: options?.merchant,
          meta: {
            url: generateURL(),
            device: generateDevice(browser),
            merchant: options?.merchant,
          },
          ...(useTimestamp
            ? {
                eventTimestamp: {
                  seconds: getTimestampInSeconds(),
                },
              }
            : {}),
        }),
      );
    },
    async trackPaymentConfiguration(
      eventName: keyof typeof paymentConfigurationConstants,
      options?: {
        action?: keyof typeof ACTION_EVENT_MAPPING;
        product?: keyof typeof PRODUCT_MAPPING;
        detail?: PaymentConfigurationDetail;
        filter?: Filter;
        merchant?: Merchant;
        useTimestamp?: boolean;
      },
    ) {
      const useTimestamp = options?.useTimestamp ?? true;

      return window.clickstream?.track(
        PaymentConfiguration.create({
          eventName: paymentConfigurationConstants[eventName],
          product: PRODUCT_MAPPING[options?.product ?? 'mokapos'],
          action: ACTION_EVENT_MAPPING[options?.action ?? 'click'],
          detail: options?.detail,
          merchant: options?.merchant,
          filter: transformFilter(options?.filter),
          meta: {
            url: generateURL(),
            device: generateDevice(browser),
            merchant: options?.merchant,
          },
          ...(useTimestamp
            ? {
                eventTimestamp: {
                  seconds: getTimestampInSeconds(),
                },
              }
            : {}),
        }),
      );
    },
    async trackIngredientLibrary(
      eventName: keyof typeof ingredientLibraryConstants,
      options?: {
        action?: keyof typeof ACTION_EVENT_MAPPING;
        product?: keyof typeof PRODUCT_MAPPING;
        detail?: IngredientLibraryDetail;
        filter?: Filter;
        merchant?: Merchant;
        useTimestamp?: boolean;
      },
    ) {
      const useTimestamp = options?.useTimestamp ?? true;

      return window.clickstream?.track(
        IngredientLibrary.create({
          eventName: ingredientLibraryConstants[eventName],
          product: PRODUCT_MAPPING[options?.product ?? 'mokapos'],
          action: ACTION_EVENT_MAPPING[options?.action ?? 'click'],
          detail: options?.detail,
          merchant: options?.merchant,
          filter: transformFilter(options?.filter),
          meta: {
            url: generateURL(),
            device: generateDevice(browser),
            merchant: options?.merchant,
          },
          ...(useTimestamp
            ? {
                eventTimestamp: {
                  seconds: getTimestampInSeconds(),
                },
              }
            : {}),
        }),
      );
    },
    async trackBilling(
      eventName: keyof typeof billingEventConstants,
      options?: {
        action?: keyof typeof ACTION_EVENT_MAPPING;
        product?: keyof typeof PRODUCT_MAPPING;
        filter?: Filter;
        changes?: Changes[];
        billing?: Billing;
        merchant?: Merchant;
        useTimestamp?: boolean;
      },
    ) {
      const useTimestamp = options?.useTimestamp ?? true;

      return window.clickstream?.track(
        Component.create({
          eventName: billingEventConstants[eventName],
          product: PRODUCT_MAPPING[options?.product ?? 'mokapos'],
          action: ACTION_EVENT_MAPPING[options?.action ?? 'click'],
          filter: transformFilter(options?.filter),
          changes: transformChangesArray(options?.changes),
          billing: options?.billing,
          meta: {
            url: generateURL(),
            device: generateDevice(browser),
            merchant: options?.merchant,
          },
          ...(useTimestamp
            ? {
                eventTimestamp: {
                  seconds: getTimestampInSeconds(),
                },
              }
            : {}),
        }),
      );
    },
    async trackAPIHealth(
      eventName: keyof typeof apiHealthEventConstants,
      options?: {
        action?: keyof typeof ACTION_EVENT_MAPPING;
        product?: keyof typeof PRODUCT_MAPPING;
        error?: ErrorDetail;
        changes?: Changes[];
        merchant?: Merchant;
        useTimestamp?: boolean;
      },
    ) {
      const useTimestamp = options?.useTimestamp ?? true;

      return window.clickstream?.track(
        APIHealth.create({
          eventName: apiHealthEventConstants[eventName],
          product: PRODUCT_MAPPING[options?.product ?? 'mokapos'],
          action: ACTION_EVENT_MAPPING[options?.action ?? 'change'],
          error: options?.error,
          changes: transformChangesArray(options?.changes),
          meta: {
            url: generateURL(),
            device: generateDevice(browser),
            merchant: options?.merchant,
          },
          ...(useTimestamp
            ? {
                eventTimestamp: {
                  seconds: getTimestampInSeconds(),
                },
              }
            : {}),
        }),
      );
    },
  };
}

const clickstreamTracker = eventTrackerFactory();

export { clickstreamTracker };
